import { DrawerActions } from "@react-navigation/native";
import { useNavigation } from "@react-navigation/native";
import React, { memo } from "react";
import {
  Image,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { useDispatch, useSelector } from "react-redux";

import ChangeLocation from "@assets/svg/DrawerIcons/ChangeLocation";
import CustSupportDrawerIcon from "@assets/svg/DrawerIcons/CustSupportDrawerIcon";
import CustomerIcon from "@assets/svg/CustomerIcon";
import KYCSetupDrawerIcon from "@assets/svg/DrawerIcons/KYCSetup";
import LanguageSelectionIcon from "@assets/svg/DrawerIcons/LanguageSelectionIcon";
import LeaderTrainingDrawerIcon from "@assets/svg/DrawerIcons/LeaderTrainingDrawerIcon";
import ProfileDrawerIcon from "@assets/svg/DrawerIcons/ProfileDrawerIcon";
import Text from "@components/AppText";
import {
  DRAWER_EVENTS,
  LOGOUT_INFO_EVENTS,
  SUPPORT_FLOW,
} from "@constants/analyticsEventsCT";
import colors from "@constants/color";
import t from "@constants/localization";
import metrics from "@constants/metrics";
import { SHOW_IJ_FLOW } from "@constants/strings";
import { removeItem } from "@lib/storage";
import { clearKycDrafts } from "../../utils/commonFunctions";
import { actions as payNowModalActions } from "@reducers/payNowModalReducer";
import analytics from "@services/analytics";
import debounce from "@utils/debounce";
import withLocation from "@utils/withLocation";
import { appVersion } from "@config";

import Logout from "@assets/svg/Logout";
import OrdersIcon from "@assets/svg/OrdersIcon";
import useLeaderState from "@hooks/useLeaderState";
import handleCMSNavigation from "@utils/handleCMSNavigation";

import { logOutCurrentDbDevice } from "@services/logout";
import { loggedInDevicesSelector } from "@selectors/auth.selector";
import testIds from "../../constants/testIds";
// Plotline widget removed

const PATH = "screens/Wallet/WalletDrawer";
const LOGGED_IN_PATH = "screens/LoggedInDevices";

const secondaryLinks = [
  {
    name: "Privacy Policy",
    navigateTo: "WebView",
    params: {
      useToken: "false",
      title: "Privacy Policy",
      showHeader: "true",
      url: "https://citymall.live/privacy-policy",
    },
  },
  {
    name: "Return Policy",
    navigateTo: "WebView",
    params: {
      useToken: "false",
      title: "Return Policy",
      showHeader: "true",
      url: "https://citymall.live/return-policy",
    },
  },
];

const WalletDrawer = memo((props) => {
  const cp_db_id = useSelector(({ auth }) => auth?.user?.cp_db_id);
  const mainLinks = cp_db_id
    ? [
        {
          name: t(`${PATH}/leaderTrainingVideo`),
          Icon: <LeaderTrainingDrawerIcon />,
          navigateTo: "LeaderTraining",
        },
        {
          name: t(`${PATH}/KYCDetails`),
          Icon: <KYCSetupDrawerIcon />,
          navigateTo: "KYCSetup",
        },
        {
          name: "Change Spoke",
          Icon: <ChangeLocation />,
          navigateTo: "SpokeChangeType",
        },
        {
          name: "Number Change Request",
          Icon: <ProfileDrawerIcon />,
          navigateTo: "PhoneChangeRequest",
        },
      ]
    : [
        {
          name: t(`${PATH}/profile`),
          Icon: <ProfileDrawerIcon />,
          navigateTo: "ProfileInfo",
        },
        {
          name: t(`${PATH}/orders`),
          Icon: <OrdersIcon />,
          navigateTo: "OrdersList",
        },
        {
          name: t(`${PATH}/otherOrders`),
          Icon: <OrdersIcon />,
          navigateTo: "OtherOrdersList",
        },
        {
          name: t(`${PATH}/customers`),
          Icon: <CustomerIcon />,
          navigateTo: "Customers",
        },
        {
          name: t(`${PATH}/KYCDetails`),
          Icon: <KYCSetupDrawerIcon />,
          navigateTo: "KYCSetup",
        },
        {
          name: t(`${PATH}/leaderTrainingVideo`),
          Icon: <LeaderTrainingDrawerIcon />,
          navigateTo: "LeaderTraining",
        },
        {
          name: t(`${PATH}/customerSupport`),
          Icon: <CustSupportDrawerIcon />,
          navigateTo: "SupportTree",
        },
        {
          name: t(`${PATH}/changeLocation`),
          Icon: <ChangeLocation />,
          navigateTo: "LocationViaAddress",
          params: {
            fromSignUp: false,
            fromProfile: false,
            fromDrawer: true,
          },
        },
        {
          name: t(`${PATH}/changeLanguage`),
          Icon: <LanguageSelectionIcon />,
          navigateTo: "LanguageSelection",
        },
      ];

  const loggedInDevices = useSelector(loggedInDevicesSelector) || {};
  const devicesCount = parseInt(loggedInDevices.devices_count ?? 0, 10);

  const link =
    "https://unwtb.app.link/Q3hbz6Jdxbb?entityId=25408&app_screen=DynamicCMSListing&app_name=cl";
  const linkType = "INTERNAL";

  const {
    name: leader_name = "",
    phone_number: leader_phone = "",
    user_image = "",
    tl_id = "",
    db_name = "",
    db_phone_number = "",
  } = useSelector(({ auth: { user } }) => user);
  const { canUseFeature } = useLeaderState();
  const dispatch = useDispatch();
  const navigation = useNavigation();

  const { MULTILANG_DISABLED = [] } = useSelector(
    (state) => state.configReducer.appSettings,
  );

  const navigateDrawerScreen = async (screenName = "", params = {}, name) => {
    const screenParams = params;
    if (screenName === "SupportTree" && !canUseFeature(["LEADER_SUSPENDED"])) {
      handleCMSNavigation(link, linkType);
      return;
    }
    if (name === "Customer Support") {
      analytics.trackEvent(SUPPORT_FLOW.SUPPORT_TICKET_STARTED, {
        source: "Hamburger menu",
      });
    }

    if (name === "Pay Now") {
      props.navigation.closeDrawer();
      dispatch(payNowModalActions.showPayNowModal());
      return;
    }

    analytics.trackEvent(DRAWER_EVENTS.HAMBURGER_ITEM_CLICKED, {
      item: screenName,
    });

    props.navigation.navigate(screenName, screenParams);
  };

  const logOut = () => {
    if (navigation) {
      navigation.navigate("LoggedInDevices");
      analytics.trackEvent(LOGOUT_INFO_EVENTS.LOGOUT_PAGE_BUTTON, {});
    }
  };
  const clearTokens = async () => {
    const tokenArray = [
      "firstTimeLoggedIn",
      "newlyCreatedUser",
      "navigateWhatsappScreenForNewUser",
      "isUserLoggedInFirst",
      "lastContactUpdate",
      "user_token",
      "streamToken",
      "bootParams",
      "userDataInAsync",
      SHOW_IJ_FLOW,
    ];
    tokenArray.forEach(async (token) => removeItem(token));
    // Drop this user's scoped KYC drafts so nothing lingers into the next login.
    clearKycDrafts(cp_db_id || tl_id);
  };
  const logOutCurrentDevice = () => {
    logOutCurrentDbDevice(tl_id)
      .then(() => {
        clearTokens();
        dispatch({ type: "RESET_REDUX" });
      })
      .catch((e) => console.log(e));
  };

  return (
    <>
      <ScrollView testID={testIds.hamburger_drawer_view}>
        <View style={{ flex: 1 }}>
          <LinearGradient
            colors={[colors.primaryv2, colors.primary]}
            start={{ x: 0.0, y: 0.1 }}
            end={{ x: 0.9, y: 0.4 }}
            style={{ padding: 20 }}
          >
            <TouchableOpacity
              testID={testIds.profile_tab}
              disabled={!!cp_db_id}
              onPress={() => {
                navigation.dispatch(DrawerActions.closeDrawer());
                navigateDrawerScreen("ProfileInfo", {}, "Profile");
              }}
              style={{ flexDirection: "row" }}
            >
              <View style={styles.imageContainer}>
                <Image
                  source={{ uri: user_image }}
                  style={styles.image}
                  resizeMode="cover"
                />
              </View>
              <View
                style={{
                  flexDirection: "column",
                  alignSelf: "center",
                  marginLeft: 12,
                }}
              >
                <Text
                  bold
                  fontType="head1"
                  numberOfLines={1}
                  style={{
                    color: colors.white,
                    width: 170,
                  }}
                >
                  {!cp_db_id ? leader_name : db_name}
                </Text>

                <Text
                  fontType="head3"
                  style={{
                    color: colors.white,
                  }}
                  bold={props.activeItemKey === "ProfileInfo"}
                >
                  +91 {!cp_db_id ? leader_phone : db_phone_number}
                </Text>
              </View>
            </TouchableOpacity>
          </LinearGradient>
          {/* Plotline widget removed */}
          <View style={[styles.bodyPadding, { marginTop: 8 }]}>
            {mainLinks.map(({ name, Icon, navigateTo, params = {} }) => {
              if (navigateTo === "LocationViaAddress") {
                return (
                  <WrappedLinkComponent
                    key={name}
                    name={name}
                    Icon={Icon}
                    navigateTo={navigateTo}
                    params={params}
                    activeItemKey={props.activeItemKey}
                  />
                );
              }

              if (
                navigateTo === "LanguageSelection" &&
                MULTILANG_DISABLED?.includes(appVersion)
              ) {
                return <React.Fragment key={name} />;
              }

              return (
                <React.Fragment key={name}>
                  <TouchableOpacity
                    style={{ paddingVertical: 10 }}
                    testID={`${navigateTo}-${testIds.tab}`}
                    onPress={() => {
                      navigation.dispatch(DrawerActions.closeDrawer());
                      navigateDrawerScreen(navigateTo, params, name);
                    }}
                  >
                    <View
                      style={[
                        styles.beLikeRow,
                        { justifyContent: "space-between" },
                      ]}
                    >
                      <View style={styles.beLikeRow}>
                        <View style={{ paddingRight: 10 }}>{Icon}</View>
                        <Text
                          style={{
                            color:
                              props.activeItemKey &&
                              props.activeItemKey === navigateTo
                                ? colors.primary
                                : colors.lightBlack,
                          }}
                          bold={props.activeItemKey === navigateTo}
                        >
                          {name}
                        </Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                </React.Fragment>
              );
            })}
          </View>

          <View style={styles.horizontal} />

          <View style={styles.bodyPadding}>
            {secondaryLinks.map(({ name, navigateTo, params }) => (
              <TouchableOpacity
                testID={`${name}-tab`}
                key={name}
                style={[
                  styles.beLikeRow,
                  { justifyContent: "space-between", paddingVertical: 10 },
                ]}
                onPress={() => {
                  navigation.dispatch(DrawerActions.closeDrawer());
                  navigateDrawerScreen(navigateTo, params, name);
                }}
              >
                <Text
                  style={{
                    color:
                      props.activeItemKey === navigateTo
                        ? colors.primary
                        : colors.extraLightBlack,
                  }}
                >
                  {name}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.horizontal} />

          <View style={[styles.bodyPadding, { paddingBottom: 6 }]}>
            <TouchableOpacity
              style={{ paddingVertical: 10 }}
              onPress={() => {
                navigation.dispatch(DrawerActions.closeDrawer());
                if (!cp_db_id) logOut();
                else logOutCurrentDevice();
              }}
              testID="wallet_drawer_logout_Button"
            >
              <View
                style={[
                  styles.beLikeRow,
                  { justifyContent: "space-between", flexDirection: "row" },
                ]}
              >
                <View>
                  <Text
                    style={{
                      color: colors.black,
                    }}
                    bold
                  >
                    {!cp_db_id
                      ? t(`${LOGGED_IN_PATH}/logoutOptions`)
                      : t(`${LOGGED_IN_PATH}/logout`)}
                  </Text>

                  {!cp_db_id ? (
                    <Text
                      style={{
                        color: colors.mediumBlacklightBlack,
                      }}
                    >
                      {!!devicesCount &&
                        t(`${LOGGED_IN_PATH}/deviceCount`, {
                          count: devicesCount,
                        })}
                    </Text>
                  ) : null}
                </View>
                <View style={{ paddingRight: 10 }}>
                  <Logout />
                </View>
              </View>
            </TouchableOpacity>
            {/* Plotline widget removed */}
          </View>
        </View>
      </ScrollView>
    </>
  );
});

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
    display: "flex",
  },
  horizontal: {
    height: 1,
    backgroundColor: colors.lighterBlack,
    marginVertical: 10,
  },
  imageContainer: {},
  image: {
    height: 60,
    width: 60,
    borderRadius: metrics.screenWidth / 1.25,
    backgroundColor: colors.primaryBg,
  },
  bodyPadding: {
    paddingHorizontal: 20,
  },
});

export default WalletDrawer;

const LinkComponent = ({ name, Icon, navigateTo, ...props }) => (
  <TouchableOpacity
    testID={`wallet_drawer_link_${name}`}
    key={name}
    style={{ paddingVertical: 10 }}
    onPress={debounce(() => props.getLocationAndNavigate(), 400)}
  >
    <View style={[styles.beLikeRow, { justifyContent: "space-between" }]}>
      <View style={styles.beLikeRow}>
        <View style={{ paddingRight: 10 }}>{Icon}</View>
        <Text
          style={{
            color:
              props.activeItemKey && props.activeItemKey === navigateTo
                ? colors.primary
                : colors.lightBlack,
          }}
          bold={props.activeItemKey === navigateTo}
        >
          {name}
        </Text>
      </View>
    </View>
  </TouchableOpacity>
);

const WrappedLinkComponent = withLocation(LinkComponent, {
  fromSignUp: false,
  fromProfile: false,
  fromDrawer: true,
});
